/**
 * Phase 40 — the pool step's pure ops on the LANES, keyed by lane id: add a
 * lane of a route (a route already in the pool gets a second lane — the
 * refusal only comes when two lanes share their sender IDs), remove, move,
 * patch its weight / capacity, and set its own senders (IDs, AI prompt
 * group). A new lane is minted with a uuid so RANDOM draws and drop→lane
 * links survive every later save.
 * For Media Devoted | Created By Ciro Autiero
 */
import { arrayMove } from '@dnd-kit/sortable';
import type { DropGenGatewayLane, DropGenLaneSenders } from '@trafficpulse/shared';

export type LaneOp =
  | { kind: 'poolAdd'; routeId: number }
  | { kind: 'poolRemove'; id: string }
  | { kind: 'poolMove'; from: number; to: number }
  | { kind: 'poolPatch'; id: string; patch: { weight?: number | null; capacity?: number | null } }
  | { kind: 'laneSenders'; id: string; patch: Partial<DropGenLaneSenders> };

/** A fresh lane of `routeId`: no weight, no cap, AI names from the container's group. */
export const newLane = (routeId: number): DropGenGatewayLane => ({
  id: crypto.randomUUID(),
  routeId,
  weight: null,
  capacity: null,
  senders: { ids: [], aiGroupId: null },
});

export function applyLaneOp(pool: DropGenGatewayLane[], op: LaneOp): DropGenGatewayLane[] {
  switch (op.kind) {
    case 'poolAdd':
      return [...pool, newLane(op.routeId)];
    case 'poolRemove':
      return pool.filter((l) => l.id !== op.id);
    case 'poolMove':
      return arrayMove(pool, op.from, op.to);
    case 'poolPatch':
      return pool.map((l) => (l.id === op.id ? { ...l, ...op.patch } : l));
    case 'laneSenders':
      return pool.map((l) => (l.id === op.id ? { ...l, senders: { ...l.senders, ...op.patch } } : l));
    default:
      return pool;
  }
}
